import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { Calendar, Clock, ArrowRight, Rss } from 'lucide-react';
import { useSanityBlogPosts } from '@/lib/sanity.hooks';
import { SectionBadge, SectionDivider } from '@/components/marketing';

export const BlogPage = () => {
  const { data: posts, loading, error } = useSanityBlogPosts();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      <SEOHead
        title="Blog | Rooflyzer OS"
        description="Latest news, updates, and insights from the Rooflyzer team. Learn about roofing industry trends, software updates, and best practices."
        canonicalUrl="/blog"
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <SectionBadge icon={Rss} text="Company News & Insights" color="cyan" className="mb-6" />

          <h1 className="hero-h1 font-bold text-white mb-5">
            Rooflyzer <span className="text-white">Blog</span>
          </h1>

          <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">
            Latest operational guides, business playbooks, software updates, and marketing insights
            from our core systems team.
          </p>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Blog Posts Grid ───────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-white/90">Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-white/90 mb-4">Failed to load blog posts.</p>
              <p className="text-white/80 text-sm">Please try again later.</p>
            </div>
          ) : !posts || posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/90 mb-4">No blog posts yet.</p>
              <p className="text-white/80 text-sm">Check back soon for updates and insights!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post._id}
                  to={`/blog/${post.slug?.current}`}
                  className="group glass-panel-interactive rounded-3xl overflow-hidden hover:scale-102 flex flex-col h-full"
                >
                  {post.featuredImage?.asset?.url && (
                    <div className="aspect-video overflow-hidden border-b border-white/10 relative">
                      <img
                        src={post.featuredImage.asset.url}
                        alt={post.featuredImage.alt || post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {post.categories?.[0] && (
                      <span className="inline-block px-3 py-1 text-xs font-mono font-medium border border-slate-700 bg-slate-800/80 text-slate-300 rounded-full mb-3 w-fit">
                        {post.categories[0].title}
                      </span>
                    )}
                    <h2 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors leading-snug">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-white/60 text-sm mb-6 line-clamp-3 leading-relaxed flex-1">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-white/40 mt-auto">
                      {post.publishedAt && (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(post.publishedAt)}
                        </div>
                      )}
                      {post.author?.name && (
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {post.author.name}
                        </div>
                      )}
                    </div>
                    <div className="mt-5 pt-4 border-t border-white/5 flex items-center text-emerald-400 text-sm font-semibold group-hover:gap-2 transition-all">
                      Read Full Post
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogPage;
